import {describeApiOutcome} from '@/utils/apiOutcome';

export const SYNC_FAILED_FALLBACK = 'De plaatsing is niet gesynced met de urenapp.';

export const EMPLOYMENT_UPDATE_FAILED = 'Er is iets misgegaan bij het syncen met hour app';

export const APP_ONBOARDING_UNKNOWN = 'Er kon niet worden gecontroleerd of de klant met de urenapp werkt, dus de plaatsing is niet gesynced.';

export const CONTRACT_CANDIDATE_NOT_FOUND = 'De kandidaat van dit contract is niet gevonden, dus de plaatsing is niet gesynced met de urenapp.';

export const HANDLED_CONTRACT_SYNC_STATUS = Object.freeze({
    SYNCED: 'synced',
    CUSTOMER_NOT_ONBOARDED: 'customer-not-onboarded',
    FAILED: 'failed',
});

function backendFirebaseId(user) {
    const id = user && user.id;

    return typeof id === 'string' && id ? id : undefined;
}

// Builds the workfix-backend `/api/user/register` payload for a flexworker
// being placed via a CandidatePlacement.
export function buildFlexworkerHourAppPayload({candidatePlacement, candidate, candidateName, customer}) {
    return {
        email: candidate.email,
        name: candidateName || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim(),
        role: 'flexworker',
        locale: 'en',
        password: 'Password123_',
        firebaseId: candidatePlacement.candidateId,
        orgFirebaseId: candidatePlacement.customerId,
        organization_name: customer && customer.name,
        clientFirebaseId: backendFirebaseId((candidatePlacement.placedBy || [])[0]),
        start_date: candidatePlacement.startDate,
        employmentFirebaseId: candidatePlacement.id,
        placement_department: candidatePlacement.department,
        placement_created_by_firebase_id: backendFirebaseId(candidatePlacement.createdBy),
    };
}

// Whether a `users/postUserHourApp` result actually synced, and if not, the reason the backend gave.
//
// The store action resolves on failure instead of rejecting, so every caller receives a value and
// none of them can tell a refusal from a success without asking. A refused sync reaches us either as
// a thrown axios error (`.response`) or as a plain response, and both carry the reason the operator
// needs: which company has no contact person, or which e-mail address is already taken.
export function describeHourAppSyncOutcome(result) {
    const outcome = describeApiOutcome(result, {fallback: SYNC_FAILED_FALLBACK});

    return {ok: outcome.ok, message: outcome.message};
}

// Builds the workfix-backend `employments/update` payload for a placement.
export function buildEmploymentUpdatePayload({placement, endDate = placement.endDate}) {
    return {
        flexworkerFirebaseId: placement.candidateId,
        employmentFirebaseId: placement.id,
        end_date: endDate || null,
    };
}

export async function syncPlacementToHourApp(dispatch, {ApiClient, placement, candidate, customer}) {
    const registration = describeHourAppSyncOutcome(await dispatch('users/postUserHourApp', {
        ApiClient,
        user: buildFlexworkerHourAppPayload({candidatePlacement: placement, candidate, customer}),
    }));

    if (!registration.ok) {
        return registration;
    }

    try {
        await dispatch('employments/update', {ApiClient, employment: buildEmploymentUpdatePayload({placement})});
    } catch (updateRefused) {
        return {ok: false, message: EMPLOYMENT_UPDATE_FAILED};
    }

    return {ok: true, message: null};
}

export function syncHandledContractPlacement(dispatch, context) {
    return attemptHandledContractSync(dispatch, context)
        .catch(() => ({status: HANDLED_CONTRACT_SYNC_STATUS.FAILED, message: SYNC_FAILED_FALLBACK}));
}

async function attemptHandledContractSync(dispatch, {ApiClient, departments, placement, candidate, customer}) {
    const onboardingCheck = await dispatch('organizations/findByCustomerId', {
        ApiClient,
        departments,
        customerId: placement.customerId,
    });

    if (!onboardingCheck.ok) {
        return {status: HANDLED_CONTRACT_SYNC_STATUS.FAILED, message: APP_ONBOARDING_UNKNOWN};
    }

    const isOnboardedForTheApp = Boolean(onboardingCheck.organization && onboardingCheck.organization.appOnboardedAt);

    if (!isOnboardedForTheApp) {
        return {status: HANDLED_CONTRACT_SYNC_STATUS.CUSTOMER_NOT_ONBOARDED, message: null};
    }

    if (!candidate) {
        return {status: HANDLED_CONTRACT_SYNC_STATUS.FAILED, message: CONTRACT_CANDIDATE_NOT_FOUND};
    }

    const sync = await syncPlacementToHourApp(dispatch, {ApiClient, placement, candidate, customer});

    return sync.ok
        ? {status: HANDLED_CONTRACT_SYNC_STATUS.SYNCED, message: null}
        : {status: HANDLED_CONTRACT_SYNC_STATUS.FAILED, message: sync.message};
}
